import i18next, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { formatDate, formatNum } from './formatting';
import { Language } from '@/constants/currencyConfig';

// Lazy server-only imports; kept out of the client bundle via `turbopack.resolveAlias`.
let nodeModulesPromise: Promise<{
	path: typeof import('path');
	fs: typeof import('fs/promises');
}> | null = null;

function loadNodeModules() {
	if (!nodeModulesPromise) {
		nodeModulesPromise = Promise.all([import('path'), import('fs/promises')]).then(
			([pathMod, fsMod]) => ({ path: pathMod.default, fs: fsMod })
		);
	}
	return nodeModulesPromise;
}

export interface IntlProps {
	i18n: { lng: Language; resources: Resource };
}

export function getLocalePackage(lang: Language) {
	switch (lang) {
		case 'cs':
			return 'cs';
		case 'sk':
			return 'sk';
		case 'hu':
			return 'hu';
		default:
			return 'cs';
	}
}

export const getIntlProps = getProjectIntlProps();

export function createIntlClient(props: IntlProps['i18n'] | undefined) {
	const inst = i18next.createInstance();

	inst.use(initReactI18next).init({
		compatibilityJSON: 'v3',
		lng: props?.lng || 'cs',
		resources: props?.resources || {},
		defaultNS: 'core',
		react: {
			transKeepBasicHtmlNodesFor: ['br'],
			transSupportBasicHtmlNodes: true,
		},
		interpolation: {
			format(value, format, lng) {
				const lang = lng || props?.lng || 'cs';

				if (format === 'formatNum') {
					return formatNum(value, undefined, lng);
				}

				if (value instanceof Date) {
					return formatDate(value, format || '', getLocalePackage(lang as Language));
				}

				return value;
			},
			escapeValue: false, // not needed for react as it escapes by default
		},
	});

	return inst;
}

function getProjectIntlProps<Resources extends string>() {
	return async (
		context: {
			__locale?: Language | undefined;
			preview?: boolean;
		},
		loadNamespaces: Resources[]
	): Promise<IntlProps> => {
		const lng: Language = context.__locale || process.env.NEXT_PUBLIC_LANGUAGE || 'cs';
		const resources: IntlProps['i18n']['resources'] = {
			[lng]: {},
		};

		const namespaces = ['core', ...loadNamespaces];
		const { path, fs } = await loadNodeModules();

		for (const ns of namespaces) {
			if (!resources[lng]) resources[lng] = {};

			const filePath = path.join(process.cwd(), 'src/lang', lng, `${ns}.json`);
			try {
				const file = await fs.readFile(filePath, { encoding: 'utf-8' });
				resources[lng][ns] = JSON.parse(file)[ns];
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(`Failed to parse ${filePath}: ${e}`);
			}
		}

		return { i18n: { lng, resources } };
	};
}

export { i18next };
